import { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import useAuth from '../hooks/useAuth';
import { obterBrandingPorSlug } from '../api/branding';
import { listarTenants } from '../api/tenants';
import { getBootstrap } from '../api/bootstrap';
import { getImpersonateUsuarioId } from '../utils/impersonation';
import i18n from 'i18next';
import { Trans } from 'react-i18next';
import { definirNomenclatura } from '../i18n';

const TenantContext = createContext(null);

/**
 * Extrai o slug do subdomínio da URL atual.
 * Ex.: cliente.konq.com.br → "cliente"
 * Em localhost/IP usa VITE_DEV_TENANT_SLUG (se definido no .env.development);
 * retorna null quando não há subdomínio real.
 */
function getSubdomainSlug() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname))
    return import.meta.env.VITE_DEV_TENANT_SLUG || null;
  const parts = hostname.split('.');
  // Precisa de ao menos 3 partes (sub.dominio.tld) e não pode ser "www"
  if (parts.length >= 3 && parts[0] !== 'www') return parts[0];
  return null;
}

/**
 * Gerencia seleção de tenant. Deve ficar dentro de AuthProvider.
 * - Se usuário tem 1 tenant → seleciona automaticamente.
 * - Se tem múltiplos → aguarda seleção explícita via selectTenant().
 * - Slug persiste em localStorage e é lido pelos interceptors do axios.
 */
function TenantProvider({ children }) {
  const { tenants, isAuthenticated, isInitialized, roleGlobal } = useAuth();

  // Subdomínio tem prioridade em produção; localStorage é o fallback para dev
  const subdomainSlug = useMemo(() => getSubdomainSlug(), []);

  const [selectedSlug, setSelectedSlug] = useState(() => subdomainSlug || localStorage.getItem('slug') || null);
  // Hidrata branding do cache (evita flash no loader inicial)
  const [branding, setBranding] = useState(() => {
    const slug = subdomainSlug || localStorage.getItem('slug');
    if (!slug) return null;
    try {
      const cached = localStorage.getItem(`branding:${slug}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [fallbackTenants, setFallbackTenants] = useState([]);
  // true quando o subdomínio aponta pra um tenant que o usuário não tem acesso
  const [semAcessoAoSubdominio, setSemAcessoAoSubdominio] = useState(false);
  const [bootstrap, setBootstrap] = useState(null);
  const [bootstrapLoaded, setBootstrapLoaded] = useState(false);

  // Quando há subdomínio, persiste imediatamente no localStorage para os interceptors
  useEffect(() => {
    if (subdomainSlug) {
      localStorage.setItem('slug', subdomainSlug);
    }
  }, [subdomainSlug]);

  // Se auth não retornou tenants, busca diretamente da API
  useEffect(() => {
    const authList = Array.isArray(tenants) ? tenants : [];
    if (!isAuthenticated || authList.length > 0) return;
    listarTenants()
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setFallbackTenants(Array.isArray(data) ? data : []);
      })
      .catch(() => setFallbackTenants([]));
  }, [tenants, isAuthenticated]);

  // Lista efetiva: preferência para tenants do auth, fallback da API
  const tenantList = useMemo(() => {
    const authList = Array.isArray(tenants) ? tenants : [];
    return authList.length > 0 ? authList : fallbackTenants;
  }, [tenants, fallbackTenants]);

  // Valida ou auto-seleciona quando a lista de tenants carrega
  useEffect(() => {
    if (!isAuthenticated || !tenantList.length) return;

    // Com subdomínio, o HOST define o tenant — ponto. Antes, se o usuário não
    // tivesse acesso ao tenant do subdomínio, o código caía no "único tenant
    // que ele tem": abrir demo.dominio mostrava os dados de OUTRO cliente sem
    // nenhum aviso. Agora não seleciona nada e sinaliza o bloqueio.
    if (subdomainSlug) {
      // Master da plataforma abre qualquer tenant, tenha ou não vínculo em
      // UsuarioTenant — é assim que ele dá suporte/impersona num concurso.
      const temAcesso = roleGlobal === 'Master' || tenantList.some((t) => t.slug === subdomainSlug);
      setSemAcessoAoSubdominio(!temAcesso);
      if (temAcesso) {
        localStorage.setItem('slug', subdomainSlug);
        setSelectedSlug(subdomainSlug);
      } else {
        localStorage.removeItem('slug');
        setSelectedSlug(null);
      }
      return;
    }

    const saved = localStorage.getItem('slug');
    const isValid = tenantList.some((t) => t.slug === saved);

    if (isValid) {
      setSelectedSlug(saved);
    } else if (tenantList.length === 1) {
      const slug = tenantList[0].slug;
      localStorage.setItem('slug', slug);
      setSelectedSlug(slug);
    } else {
      localStorage.removeItem('slug');
      setSelectedSlug(null);
    }
  }, [tenantList, isAuthenticated, subdomainSlug, roleGlobal]);

  // Busca branding sempre que o slug selecionado mudar
  useEffect(() => {
    if (!selectedSlug) {
      setBranding(null);
      return;
    }
    obterBrandingPorSlug(selectedSlug)
      .then((res) => {
        const data = res.data?.data ?? res.data ?? null;
        setBranding(data);
        if (data) {
          try {
            localStorage.setItem(`branding:${selectedSlug}`, JSON.stringify(data));
          } catch {
            /* quota / disabled */
          }
        }
      })
      .catch(() => setBranding(null));
  }, [selectedSlug]);

  // Busca bootstrap (extraído pra reuso — útil depois de concluir cadastro)
  const refreshBootstrap = useCallback(async () => {
    // Master sem impersonar pula bootstrap (cai no chooser /dashboard). Master
    // impersonando carrega normalmente — backend devolve o bootstrap do impersonado.
    const impersonando = !!getImpersonateUsuarioId();
    const masterSemImpersonar = roleGlobal === 'Master' && !impersonando;
    if (!isInitialized || !isAuthenticated || !selectedSlug || masterSemImpersonar) {
      setBootstrap(null);
      setBootstrapLoaded(masterSemImpersonar ? true : false);
      return;
    }
    setBootstrapLoaded(false);
    try {
      const res = await getBootstrap();
      setBootstrap(res.data?.data ?? res.data ?? null);
    } catch {
      setBootstrap(null);
    } finally {
      setBootstrapLoaded(true);
    }
  }, [isInitialized, isAuthenticated, selectedSlug, roleGlobal]);

  // Chama bootstrap quando autenticado e slug disponível
  useEffect(() => {
    refreshBootstrap();
  }, [refreshBootstrap]);

  // Termos do cliente (ex.: "Case"/"Cooperativa") nas traduções pt; null = padrão.
  // Vêm do branding (por subdomínio), então valem antes do login também.
  useEffect(() => {
    definirNomenclatura(branding?.nomenclaturaJson ?? null);
  }, [branding]);

  const selectTenant = useCallback(
    (slug) => {
      // Em produção (subdomínio fixo) não permite trocar de tenant manualmente
      if (subdomainSlug) return;
      localStorage.setItem('slug', slug);
      setSelectedSlug(slug);
    },
    [subdomainSlug],
  );

  const clearTenant = useCallback(() => {
    localStorage.removeItem('slug');
    setSelectedSlug(null);
  }, []);

  const currentTenant = tenantList.find((t) => t.slug === selectedSlug) || null;
  // Role do usuário no tenant selecionado (ex: "Produtor", "Jurado", "Curador")
  const roleTenant = currentTenant?.role ?? null;

  // Sem acesso ao tenant do subdomínio: mensagem explícita. As duas alternativas
  // são piores — cair em outro tenant mostra dados do cliente errado, e deixar
  // passar sem slug rende tela vazia sem explicação.
  if (isAuthenticated && semAcessoAoSubdominio) {
    return (
      <div
        style={{
          padding: 32,
          fontFamily: 'system-ui, sans-serif',
          maxWidth: 560,
          margin: '10vh auto',
          textAlign: 'center',
        }}
      >
        <h2 style={{ marginBottom: 8 }}>{i18n.t('semAcessoTenant.titulo')}</h2>
        <p style={{ color: '#555', lineHeight: 1.5 }}>
          <Trans i18nKey="semAcessoTenant.texto" values={{ slug: subdomainSlug }} components={{ b: <strong /> }} />
        </p>
      </div>
    );
  }

  return (
    <TenantContext.Provider
      value={{
        tenants: tenantList,
        selectedSlug,
        semAcessoAoSubdominio,
        subdomainSlug,
        currentTenant,
        roleTenant,
        branding,
        bootstrap,
        bootstrapLoaded,
        refreshBootstrap,
        selectTenant,
        clearTenant,
        needsSelection: isAuthenticated && tenantList.length > 1 && !selectedSlug,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}

export { TenantContext, TenantProvider };
